import React, { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    // Load from local storage on init
    const savedCart = localStorage.getItem("stmj_cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    // Save to local storage on change
    localStorage.setItem("stmj_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, notes = "") => {
    setCart((prev) => {
      const existing = prev.find(
        (item) => item.product.id === product.id && item.notes === notes
      );
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id && item.notes === notes
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1, notes }];
    });
  };

  const removeFromCart = (productId, notes = "") => {
    setCart((prev) =>
      prev.filter(
        (item) => item.product.id !== productId || item.notes !== notes
      )
    );
  };

  const updateQuantity = (productId, quantity, notes = "") => {
    if (quantity <= 0) {
      removeFromCart(productId, notes);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId && item.notes === notes
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => setCart([]);

  const subtotal = cart.reduce(
    (acc, item) => acc + item.product.price * item.quantity,
    0
  );
  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    subtotal,
    totalItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
